import { CampaignDeliverySummary, type CampaignDeliverySummaryProps } from '@/components/campaign-delivery-summary';
import { ForecastChart, compactCount } from '@/components/forecast-chart';
import { useEffect, useState } from 'react';
import { fmt } from '@/components/primitives';
import { PublishSequence } from '@/components/publish-sequence';
import { fmtDayShort } from '@/lib/datetime';
import type { Channel } from '@/api/types';
import type { CampaignForecast, CampaignForecastInput } from '@/api/manage-types';
import type { CampaignFlight } from '@/lib/campaign-flights';
import type { PublishStep } from '@/lib/campaign-publish-sequence';
import type { LegacyManageProduction } from '@/lib/manage-screen-adapters';

/**
 * The forecast's sales: the budget at the CPA and ROAS Reports shows for this
 * production on this channel over the last 180 days. CPA and ROAS are those
 * figures exactly, never recomputed from a rounded ticket count; null when the
 * history has no attributed sales (or the server predates these fields).
 */
export function forecastSales(
  forecast: Pick<CampaignForecast, 'assumptions' | 'projection'> | undefined,
): { tickets: number; value_cents: number | null; cpa_cents: number | null; roas: number | null } | null {
  const tickets = forecast?.projection?.projected_tickets;
  if (tickets == null || !Number.isFinite(tickets)) return null;
  return {
    tickets,
    value_cents: forecast?.projection?.projected_revenue_cents ?? null,
    cpa_cents: forecast?.assumptions?.cpa_cents ?? null,
    roas: forecast?.assumptions?.roas ?? null,
  };
}

/** The parts of the forecast query the panel reads. */
export interface ForecastQueryState {
  data: CampaignForecast | undefined;
  isPending: boolean;
  isError: boolean;
  error?: Error | null;
}

export interface ForecastPanelProps {
  /** The production picked, or null before one is; only its presence is read. */
  production: Pick<LegacyManageProduction, 'id' | 'title'> | null | undefined;
  channel: Channel | null;
  forecastQuery: ForecastQueryState;
  /** The request the query was built from; null when the form cannot build one. */
  forecastRequest?: CampaignForecastInput | null;
  forecastNote?: string | null;
  publishSteps?: PublishStep[];
  /** A cold failure reading the preflight or the sync status. */
  publishReadUnavailable?: boolean;
  publishSettled?: boolean;
  /** The live campaign's delivery strip; null before it has left draft. */
  delivery?: CampaignDeliverySummaryProps | null;
  flights?: readonly CampaignFlight[];
  /** Bumped to open the publish log and scroll it into view. */
  openRequest?: number;
}

export function ForecastPanel({
  production,
  channel,
  forecastQuery,
  forecastRequest = undefined,
  forecastNote = null,
  publishSteps = [],
  publishReadUnavailable = false,
  publishSettled = false,
  delivery = null,
  flights = [],
  openRequest = 0,
}: ForecastPanelProps) {
  const forecast = forecastQuery.data;
  const projection = forecast?.projection;
  const assumptions = forecast?.assumptions;
  const series = forecast?.series_forecast ?? null;
  const sales = forecastSales(forecast);
  const frequency = projection?.average_frequency ?? null;
  const seriesEnd = series?.series.at(-1)?.day ?? null;
  const channelName = channel ? {meta:'Meta',ctv:'CTV',dsp:'Display'}[channel] : '';
  // Only a borrowed history gets a sentence: the campaign's own days need
  // no explanation, and the column carries no prose it does not need (Gio,
  // 2026-09-18).
  const basisSentence = series
    ? series.basis === 'own'
      ? null
      : series.basis === 'production'
        ? `No delivery of its own yet: projected from ${series.basis_days} reported day${series.basis_days === 1 ? '' : 's'} of this production's other ${channelName} campaigns.`
        : `No delivery for this production yet: projected from ${series.basis_days} reported day${series.basis_days === 1 ? '' : 's'} across every ${channelName} campaign you can see.`
    : null;
  // The publish log is history once the publish has settled and nothing is
  // running; it opens itself again the moment a step is active or failed.
  const publishInFlight = publishSteps.some((step) => step.state === 'active' || step.state === 'failed');
  const [logOpen, setLogOpen] = useState(false);
  useEffect(() => {
    if (!openRequest) return;
    setLogOpen(true);
    requestAnimationFrame(() => document.getElementById('publish-log')?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
  }, [openRequest]);
  const logCollapsed = publishSettled && !publishInFlight && !logOpen;
  const doneSteps = publishSteps.filter((step) => step.state === 'done').length;

  return (
    <div>
      {delivery && <CampaignDeliverySummary {...delivery} />}
      <div className="forecast">
        <h4><span className="pulse"/>Performance Forecast</h4>
        {!production || !channel ? (
          <div className="muted" style={{fontSize:11.5,lineHeight:1.5}}>
            Select a production and channel to load a historical forecast.
          </div>
        ) : forecast ? (
          series ? (
            <>
              <div className="fc-big-lbl">Projected impressions{seriesEnd ? ` by ${fmtDayShort(seriesEnd)}` : ''}</div>
              <div className="forecast-headline tnum">{compactCount(series.projected_impressions)}</div>
              {compactCount(series.low_impressions) !== compactCount(series.high_impressions) && (
                <div className="forecast-range tnum">
                  range {compactCount(series.low_impressions)} – {compactCount(series.high_impressions)} · p10–p90 of {series.band_days ?? series.basis_days} days
                </div>
              )}
              {series.high_impressions > 0 ? (
                <div style={{marginTop:10}}>
                  <ForecastChart
                    series={series.series}
                    today={series.today}
                    flightStarts={(flights || []).map((flight) => flight.start).filter(Boolean)}
                    label={`Cumulative impressions, reported and projected to ${seriesEnd ? fmtDayShort(seriesEnd) : 'the end of the flight'}`}
                  />
                </div>
              ) : (
                /* A zero projection drew an empty chart with gridlines at 0.2
                   impressions (F5 UX P1). */
                <div className="muted" style={{fontSize:11.5,lineHeight:1.5,marginTop:6}}>
                  No delivery projected for these dates.
                </div>
              )}
              <div className="fc-row" style={{marginTop:6}}><span>Clicks</span><b>{series.projected_clicks.toLocaleString()}</b></div>
              <div className="fc-row">
                <span>Cost</span>
                <b>{fmt.$full(series.projected_spend_cents / 100)} <span className="muted" style={{fontWeight:400}}>of {fmt.$full(series.budget_cents / 100)}{series.budget_cents > 0 ? ` · ${Math.round((series.projected_spend_cents / series.budget_cents) * 100)}%` : ''}</span></b>
              </div>
              <div className="fc-row"><span>CPM</span><b>${(series.cpm_cents / 100).toFixed(2)}</b></div>
              {channel !== 'ctv' && <div className="fc-row"><span>CTR</span><b>{(series.ctr * 100).toFixed(2)}%</b></div>}
              {series.pace && series.pace.unspent_cents > 0 && (
                <div className="fc-row"><span>Left unspent at this pace</span><b style={{color:'var(--warn)'}}>{fmt.$full(series.pace.unspent_cents / 100)}</b></div>
              )}
              {basisSentence && <div className="forecast-basis">{basisSentence}</div>}
            </>
          ) : (
            <>
              {forecast.series_unavailable && (
                /* The per-day model refused rather than projecting from a day
                   or two; say how far it is from the floor instead of showing
                   nothing where the chart would be. */
                <div className="muted" style={{fontSize:11.5,lineHeight:1.5,marginBottom:6}}>
                  {forecast.series_unavailable.reason === 'insufficient_complete_days'
                    ? `Not enough complete delivery days to project yet (${forecast.series_unavailable.complete_days} of ${forecast.series_unavailable.required_days}).`
                    : 'No projection for these flights yet.'}
                </div>
              )}
              {!forecast.available || !projection ? (
                <div className="muted" style={{fontSize:11.5,lineHeight:1.5}}>
                  No forecast yet — this production and channel do not have enough
                  delivery history.
                </div>
              ) : (
                <>
                  <div className="fc-big-lbl">EST. TICKETS</div>
                  <div className="fc-big tnum">{sales ? Math.round(sales.tickets).toLocaleString() : '—'}</div>
                  {assumptions && (
                    <div className="muted mt-1" style={{fontSize:10.5}}>
                      Based on observed delivery from {assumptions.history_from} to {assumptions.history_to}
                    </div>
                  )}
                </>
              )}
            </>
          )
        ) : forecastRequest === null ? (
          /* A request that cannot be built is not a request in flight. Until
             2026-09-18 this case fell through to "Loading historical
             delivery…" and stayed there for the life of the tab. */
          <div className="muted" style={{fontSize:11.5,lineHeight:1.5}}>
            {forecastNote || 'Set a budget and dates to forecast this campaign.'}
          </div>
        ) : forecastQuery.isPending ? (
          <div className="muted" style={{fontSize:11.5}}>Loading historical delivery…</div>
        ) : forecastQuery.isError ? (
          <div style={{fontSize:11.5,color:'var(--bad)',lineHeight:1.5}}>
            Forecast unavailable. {forecastQuery.error?.message || 'The request failed.'}
          </div>
        ) : (
          <div className="muted" style={{fontSize:11.5,lineHeight:1.5}}>
            No forecast yet — this production and channel do not have enough
            delivery history.
          </div>
        )}
        {forecast && forecastNote && (
          <div className="muted mt-1" style={{fontSize:10.5,lineHeight:1.5}}>{forecastNote}</div>
        )}
      </div>
      {projection && (!series || frequency !== null) && (
      <div className="forecast">
        <h4>Reach &amp; delivery</h4>
        {/* With a per-day projection above, its impressions and clicks are the
            column's one answer: these came from the older whole-campaign model
            and could contradict it, 0 above and 2.81M here (F5 UX P1). */}
        {!series && <div className="fc-row"><span>Impressions</span><b>{fmt.n(projection.impressions)}</b></div>}
        {frequency !== null && (
          <div className="fc-row"><span>Avg. frequency</span><b>{`${frequency.toFixed(1)}×`}</b></div>
        )}
        {!series && channel !== 'ctv' && <div className="fc-row"><span>Clicks</span><b>{fmt.n(projection.clicks)}</b></div>}
        {/* One CPM on the page: the series block already quotes the fully
            loaded CPM when a per-day projection exists. */}
        {!series && assumptions && (
          <div className="fc-row"><span>CPM</span><b>{`$${(assumptions.cpm_cents / 100).toFixed(2)}`}</b></div>
        )}
      </div>
      )}
      {projection && (
      <div className="forecast">
        <h4>Tickets and return</h4>
        {sales ? (
          <>
            <div className="fc-row"><span>Est. tickets</span><b>{Math.round(sales.tickets).toLocaleString()}</b></div>
            <div className="fc-row"><span>Ticket Value</span><b>{sales.value_cents === null ? '—' : fmt.$full(sales.value_cents / 100)}</b></div>
            <div className="fc-row"><span>CPA</span><b>{sales.cpa_cents === null ? '—' : `$${(sales.cpa_cents / 100).toFixed(2)}`}</b></div>
            <div className="fc-row">
              <span>ROAS</span>
              {sales.roas === null ? <b>—</b> : (
                <b style={{color: sales.roas >= 3 ? 'var(--good)' : sales.roas >= 1.5 ? 'var(--warn)' : 'var(--bad)'}}>
                  {sales.roas.toFixed(2)}×
                </b>
              )}
            </div>
            <div className="muted" style={{fontSize:10.5,lineHeight:1.5,marginTop:4}}>
              At this production&apos;s {channelName} CPA and ROAS in Reports, last 180 days.
            </div>
          </>
        ) : (
          /* Never a zero: no attributed history is not a campaign that will
             sell nothing. */
          <div className="muted" style={{fontSize:11.5,lineHeight:1.5}}>No sales history yet</div>
        )}
      </div>
      )}
      {projection && (
      <div className="forecast">
        <h4>Agency margin</h4>
        <div className="fc-row"><span>Client budget (gross)</span><b>{fmt.$full(projection.gross_budget_cents / 100)}</b></div>
        {projection.agency_margin_to_date_cents !== null && projection.agency_margin_to_date_cents !== undefined && (
          <div className="fc-row">
            <span>Margin to date</span>
            <b>{fmt.$full(projection.agency_margin_to_date_cents / 100)}{projection.agency_margin_to_date_cents === 0 && projection.agency_margin_cents === 0 ? <span className="muted" style={{fontWeight:400}}> · margin off</span> : null}</b>
          </div>
        )}
        <div className="fc-row"><span>Margin over the flight</span><b>{fmt.$full(projection.agency_margin_cents / 100)}{projection.agency_margin_cents === 0 ? <span className="muted" style={{fontWeight:400}}> · margin off</span> : null}</b></div>
      </div>
      )}
      <div className="forecast" id="publish-log" style={{borderBottom: 0}}>
        {logCollapsed || (publishSettled && !publishInFlight) ? (
          <button
            type="button"
            className="publish-log-toggle"
            aria-expanded={!logCollapsed}
            onClick={() => setLogOpen((value) => !value)}
          >
            <h4>Publish log</h4>
            <span className="publish-log-summary">
              {doneSteps} of {publishSteps.length} steps done · {logCollapsed ? 'show' : 'hide'}
            </span>
          </button>
        ) : (
          <h4>{publishInFlight && <span className="pulse"/>}Publishing</h4>
        )}
        {/* Without this, a read that failed cold renders as a column of
            `pending` steps — which is exactly how a campaign that has not
            started yet looks. Saying nothing would be the panel asserting all
            is well on no evidence at all. */}
        {publishReadUnavailable && (
          <div className="muted" style={{fontSize:11.5,lineHeight:1.5,color:'var(--warn)',marginBottom:8}}>
            Could not read the publish status — retrying. The steps below may be out of date.
          </div>
        )}
        {publishSteps && publishSteps.length > 0 ? (
          !logCollapsed && <PublishSequence steps={publishSteps} dense/>
        ) : (
          /* Before a campaign is saved there is no id to ask about, so there is
             genuinely nothing to report — say that, rather than describing the
             mechanism. */
          <div className="muted" style={{fontSize:11.5,lineHeight:1.5}}>
            Save the campaign to follow its path to serving here.
          </div>
        )}
      </div>
    </div>
  );
}
